import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import './AddDriver.scss';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { FLEET_DRIVERS_URL, FLEET_CARS_URL } from '../../../api/urls';
import { useApiFetch } from '../../../api/fetcher';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Steps } from 'primereact/steps';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { AddNewDriver, AddNewDriverCar } from '../../../types/fleetCarTypes';
import { InputMask } from "primereact/inputmask";
import { getPojazdLabel } from '../../../utils/cars';

const AddDriver: React.FC = () => {
    const navigate = useNavigate();
    const apiFetch = useApiFetch();
    const toast = React.useRef<Toast>(null);

    // Step management
    const [activeStep, setActiveStep] = useState(0);
    const [loading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [driverData, setDriverData] = useState<AddNewDriver>({
        phone_number: '',
        first_name: ''
    });
    const [selectedCarIds, setSelectedCarIds] = useState<number[]>([]);

    const [cars, setCars] = useState<AddNewDriverCar[]>([]);
    const [carsLoading, setCarsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const steps = [
        { label: 'Dane kierowcy' },
        { label: 'Przypisz pojazd' },
        { label: 'Potwierdź dane' }
    ];

    useEffect(() => {
        if (activeStep === 1) {
            loadCars();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep]);

    const loadCars = async () => {
        setCarsLoading(true);
        try {
            const res = await apiFetch(FLEET_CARS_URL, { credentials: 'include' });
            const json = await res.json();
            const carsData: AddNewDriverCar[] = Array.isArray(json?.results) ? json.results : (Array.isArray(json) ? json : []);
            setCars(carsData);
        } catch (error) {
            console.error('Error loading cars:', error);
        } finally {
            setCarsLoading(false);
        }
    };

    const handleNext = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    const handleCarSelection = (carId: number, checked: boolean) => {
        setSelectedCarIds(prev =>
            checked ? [...prev, carId] : prev.filter(id => id !== carId)
        );
    };

    const handleSaveDriver = async () => {
        setSaving(true);
        const payload_phone_number = "+48" + driverData.phone_number.replace(/^\(\+48\)\s?/, '').replace(/-/g, '');
        try {
            const payload = {
                phone_number: payload_phone_number,
                first_name: driverData.first_name,
                car_ids: selectedCarIds
            };

            const response = await apiFetch(FLEET_DRIVERS_URL, {
                method: 'POST',
                body: JSON.stringify(payload),
                credentials: 'include',
            });

            if (response.ok) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Sukces',
                    detail: 'Dodano nowego kierowcę',
                    life: 3000
                });
                setTimeout(() => {
                    navigate('/fleet/', { state: { activeTab: 'drivers' } });
                }, 1000);
            } else {
                const errorData = await response.json();
                let phone_number_message = errorData?.phone_number[0];
                if (typeof phone_number_message === 'string') {
                    if (phone_number_message === 'Driver with this phone number already exists for this account.') {
                        phone_number_message = 'Kierowca z tym numerem telefonu jest już przypisany do tego konta.';
                    } else if (phone_number_message === 'Driver with this phone number exists for a different account.') {
                        phone_number_message = 'Kierowca z tym numerem telefonu jest już przypisany do innego konta. Jeśli potrzebujesz pomocy, skontaktuj się z naszym zespołem.';
                    }
                }
                const errorMessage = phone_number_message || errorData?.detail || 'Wystąpił błąd podczas dodawania kierowcy';
                toast.current?.show({
                    severity: 'error',
                    summary: 'Błąd',
                    detail: errorMessage,
                    life: 5000
                });
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Błąd',
                detail: 'Wystąpił błąd podczas dodawania kierowcy',
                life: 5000
            });
        } finally {
            setSaving(false);
        }
    };

    const filteredCars = cars.filter(car =>
        car.plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${car.maker_name} ${car.model_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const renderStep1 = () => (
        <div className="text-center">
            <div className="">
                <img alt='driver' className="logo" style={{ height: '70px' }} src={`/fleet/driver/person.svg`} />
            </div>
            <h2 className="mb-3">Dane kierowcy</h2>
            <p className="text-muted mb-5">Wprowadź podstawowe informacje o nowym kierowcy</p>

            <div className="p-fluid" style={{ maxWidth: '400px', margin: '0 auto' }}>
                <div className="field mb-3">
                    <span className="p-input-icon-left">
                        <InputMask
                            value={driverData.phone_number}
                            onChange={(e) => setDriverData(prev => ({ ...prev, phone_number: e.target.value ? e.target.value : '' }))}
                            id="phone"
                            mask="(+48) 999-999-999"
                            placeholder="(+48) 999-999-999"
                        ></InputMask>
                    </span>
                    <small className="text-muted">Numer telefonu*</small>
                </div>

                <div className="field">
                    <span className="p-input-icon-left">
                        <InputText
                            value={driverData.first_name}
                            onChange={(e) => setDriverData(prev => ({ ...prev, first_name: e.target.value }))}
                            placeholder="Nazwa kierowcy"
                            className="w-100"
                        />
                    </span>
                    <small className="text-muted">Nazwa kierowcy*</small>
                </div>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="text-center">
            <div className="">
                <img alt='driver' className="logo" style={{ height: '70px' }} src={`/fleet/car/car.svg`} />
            </div>
            <h2 className="mb-3">Przypisz pojazdy do kierowcy</h2>
            <p className="text-muted mb-4">Wybierz pojazdy, do których kierowca będzie miał dostęp</p>

            <div className="mb-3">
                <span className="p-input-icon-left w-100">
                    <InputText
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Szukaj"
                        className="w-100"
                    />
                </span>
            </div>

            <div className="mb-3">
                <small className="text-muted">
                    Znaleziono {cars.length} {getPojazdLabel(cars.length)} • {selectedCarIds.length} wybrano
                </small>
            </div>

            {carsLoading ? (
                <div className="text-center">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                </div>
            ) : (
                <div className="vehicle-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <div className="vehicle-header d-flex justify-content-between p-2 border-bottom">
                        <span><strong>Numer rejestracyjny</strong></span>
                        <span><strong>Marka i model</strong></span>
                    </div>
                    {filteredCars.map((car) => (
                        <div key={car.id} className="vehicle-item d-flex justify-content-between align-items-center p-2 border-bottom">
                            <div className="d-flex align-items-center">
                                <Checkbox
                                    checked={selectedCarIds.includes(car.id)}
                                    onChange={(e) => handleCarSelection(car.id, e.checked || false)}
                                    className="me-3"
                                />
                                <span>{car.plate}</span>
                            </div>
                            <span>{`${car.maker_name} ${car.model_name}`}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const renderStep3 = () => (
        <div className="text-center">
            <div className="">
                <img alt='driver-summary' className="logo" style={{ height: '70px' }} src={`/fleet/driver/add-driver-summary.svg`} />
            </div>
            <h2 className="mb-3">Potwierdź dane</h2>
            <p className="text-muted mb-4">Sprawdź wprowadzone informacje przed dodaniem kierowcy</p>

            <div className="row">
                <div className='d-flex row justify-content-center'>
                    <div className='first'>
                        <h6 className="mb-2 d-flex" style={{ fontWeight: 500, color: '#23272E' }}>Informacje o kierowcy</h6>
                        <div
                            style={{
                                background: '#F7F9FB',
                                border: '1px solid #E0E4EF',
                                borderRadius: '8px',
                                padding: '18px 20px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                                maxWidth: 420,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ color: '#7A8699', fontSize: 15, fontWeight: 400, minWidth: 120 }}>
                                    Nazwa kierowcy
                                </span>
                                <span style={{ color: '#23272E', fontWeight: 600, fontSize: 15 }}>
                                    {driverData.first_name}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ color: '#7A8699', fontSize: 15, fontWeight: 400, minWidth: 120 }}>
                                    Numer telefonu
                                </span>
                                <span style={{ color: '#23272E', fontWeight: 700, fontSize: 15 }}>
                                    {driverData.phone_number}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='d-flex row justify-content-center mt-4'>
                    <div className='first'>
                        <h6 className="mb-2 d-flex" style={{ fontWeight: 500, color: '#23272E' }}>Przypisane samochody</h6>
                        <div
                            style={{
                                background: '#F7F9FB',
                                border: '1px solid #E0E4EF',
                                borderRadius: '8px',
                                padding: '18px 20px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                                maxWidth: 420,
                            }}
                        >
                            <div>
                                {selectedCarIds.length > 0 ? (
                                    cars
                                        .filter(car => selectedCarIds.includes(car.id))
                                        .map((car) => (
                                            <div key={car.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ color: '#23272E', fontSize: 15, fontWeight: 600, minWidth: 120 }}>
                                                    {car.plate}
                                                </span>
                                                <span style={{ color: '#7A8699', fontWeight: 400, fontSize: 15 }}>
                                                    {`${car.maker_name} ${car.model_name}`}
                                                </span>
                                            </div>
                                        ))
                                ) : (
                                    <p className="text-muted">Brak przypisanych pojazdów</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch (activeStep) {
            case 0:
                return renderStep1();
            case 1:
                return renderStep2();
            case 2:
                return renderStep3();
            default:
                return renderStep1();
        }
    };

    const canProceed = () => {
        switch (activeStep) {
            case 0:
                return driverData.phone_number.trim() && driverData.first_name.trim();
            case 1:
                return true;
            case 2:
                return true;
            default:
                return false;
        }
    };

    return (
        <div className="card-margins">
            <Toast ref={toast} />

            <div className="d-flex justify-content-between align-items-center panel-margin-top">
                <Card className="d-flex w-100 justify-content-between card-border">
                    <div className="d-flex justify-content-end w-100">
                        <div className="d-flex">
                            <Button
                                icon="pi pi-arrow-left"
                                className="p-button-text"
                                onClick={() => navigate('/fleet/', { state: { activeTab: 'drivers' } })}
                            />
                            <div className="ml-2">
                                <h2 className="m-0">Nowy kierowca</h2>
                                <div className="text-muted">Stwórz nowego kierowcę</div>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            <Card className="card-border panel-margin-top-with-margin">
                <Steps
                    model={steps}
                    activeIndex={activeStep}
                    readOnly={true}
                    className="mb-4"
                />

                <div className="step-content">
                    {renderCurrentStep()}
                </div>

                <div className="d-flex justify-content-end mt-4 below-buttons">
                    {activeStep > 0 && (
                        <Button
                            label="Wstecz"
                            icon="pi pi-arrow-left"
                            className="p-button-secondary me-2 back-button"
                            onClick={handleBack}
                            disabled={loading || saving}
                        />
                    )}

                    {activeStep < steps.length - 1 ? (
                        <Button
                            label="Dalej"
                            className="next-button"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            onClick={handleNext}
                            disabled={!canProceed() || loading || saving}
                        />
                    ) : (
                        <Button
                            label="Potwierdź i dodaj kierowce"
                            className="next-button"
                            iconPos="right"
                            onClick={handleSaveDriver}
                            loading={saving}
                            disabled={!canProceed() || loading}
                        />
                    )}
                </div>
            </Card>
        </div>
    );
};

export default AddDriver;